import { DEFAULT_LISTEN_SELECTOR, FrameAttribute } from "./const";
import { FrameConfig, RemoteFrameRefreshPayload } from "./types";
import { parseBoolean, parseCredentials, parsePreserveState, splitCsv, toNumber } from "./utils";

/**
 * Resolves effective frame configuration from element attributes and payload overrides.
 *
 * @param frame - The remote-frame host element.
 * @param payload - Optional per-refresh overrides.
 * @returns The normalized runtime configuration.
 */
export function resolveFrameConfig(
  frame: HTMLElement,
  payload: RemoteFrameRefreshPayload
): FrameConfig {
  const rawSelect = (payload.select ?? frame.getAttribute(FrameAttribute.Select) ?? "outer").toLowerCase();
  const rawCache = (payload.cache ?? frame.getAttribute(FrameAttribute.Cache) ?? "default").toLowerCase();
  const rawHistory = (payload.history ?? frame.getAttribute(FrameAttribute.History) ?? "none").toLowerCase();
  const rawHistoryScope = (payload.historyScope ?? frame.getAttribute(FrameAttribute.HistoryScope) ?? "query")
    .trim()
    .toLowerCase();
  const explicitUrlBase = payload.urlBase ?? frame.getAttribute(FrameAttribute.UrlBase);
  const rawUrlBase = (explicitUrlBase ?? "src").toLowerCase();
  const rawExecuteScripts =
    (payload.executeScripts ?? frame.getAttribute(FrameAttribute.ExecuteScripts) ?? "none").toLowerCase();
  const rawBroadcastPrefix = (frame.getAttribute(FrameAttribute.BroadcastEventPrefix) ?? "remote-frame-refreshed").trim();
  const listenRefreshFrom = splitCsv(frame.getAttribute(FrameAttribute.ListenRefreshFrom) ?? "");
  const rawListenRefreshUrlMode = (frame.getAttribute(FrameAttribute.ListenRefreshUrlMode) ?? "query-merge")
    .trim()
    .toLowerCase();
  const listenHistory = parseListenHistory(frame.getAttribute(FrameAttribute.ListenHistory));
  const listenRefreshQueryKeys = splitCsv(frame.getAttribute(FrameAttribute.ListenRefreshQueryKeys) ?? "");
  const broadcastAttr = frame.getAttribute(FrameAttribute.BroadcastRefresh);
  const rawBroadcastAttr = (broadcastAttr ?? "").trim().toLowerCase();

  const sourceSelector = (payload.sourceSelector ?? frame.getAttribute(FrameAttribute.SourceSelector) ?? "").trim();

  let broadcastMode: FrameConfig["broadcastMode"] = "none";
  if (broadcastAttr !== null) {
    if (rawBroadcastAttr === "" || parseBoolean(rawBroadcastAttr)) {
      broadcastMode = "load";
    } else {
      const broadcastTokens = splitCsv(rawBroadcastAttr);
      const wantsLoad = broadcastTokens.includes("load");
      const wantsSubmit = broadcastTokens.includes("submit");

      if (wantsLoad && wantsSubmit) {
        broadcastMode = "load-submit";
      } else if (wantsLoad) {
        broadcastMode = "load";
      } else if (wantsSubmit) {
        broadcastMode = "submit";
      }
    }
  }

  const listenRefreshAny = listenRefreshFrom.includes("*") || listenRefreshFrom.includes("any");
  const broadcastEventPrefix = rawBroadcastPrefix.replace(/:+$/, "") || "remote-frame-refreshed";
  const listenRefreshUrlMode: FrameConfig["listenRefreshUrlMode"] =
    rawListenRefreshUrlMode === "full" || rawListenRefreshUrlMode === "query-replace"
      ? rawListenRefreshUrlMode
      : "query-merge";

  const src = (payload.src ?? frame.getAttribute(FrameAttribute.Src) ?? "").trim();
  const shopify = payload.shopify ?? frame.hasAttribute(FrameAttribute.Shopify);

  // In shopify mode with no explicit src or url-base, default to current page URL.
  const urlBase: FrameConfig["urlBase"] =
    rawUrlBase === "current"
      ? "current"
      : !explicitUrlBase && shopify && !src
        ? "current"
        : "src";

  return {
    src,
    sourceSelector,
    selector: (payload.selector ?? frame.getAttribute(FrameAttribute.Selector) ?? "").trim(),
    select: rawSelect === "inner" ? "inner" : "outer",
    shopify,
    sectionId: (payload.sectionId ?? frame.getAttribute(FrameAttribute.SectionId) ?? "").trim(),
    cache: rawCache === "bypass" || rawCache === "reload" ? rawCache : "default",
    cacheTtlMs: toNumber(frame.getAttribute(FrameAttribute.CacheTtlMs), 10_000),
    cacheMaxEntries: Math.max(1, toNumber(frame.getAttribute(FrameAttribute.CacheMaxEntries), 200)),
    urlBase,
    query: frame.getAttribute(FrameAttribute.Query) ?? "",
    listenHistory,
    queryMode: payload.queryMode === "replace" ? "replace" : "merge",
    history: rawHistory === "replace" || rawHistory === "push" ? rawHistory : "none",
    historyScope: rawHistoryScope === "full" ? "full" : "query",
    historyParams: splitCsv(frame.getAttribute(FrameAttribute.HistoryParams) ?? ""),
    preserveState: parsePreserveState(payload.preserveState ?? frame.getAttribute(FrameAttribute.PreserveState)),
    loadingClass: (frame.getAttribute(FrameAttribute.LoadingClass) ?? "is-loading").trim() || "is-loading",
    listenEvents: splitCsv(frame.getAttribute(FrameAttribute.ListenEvents) ?? ""),
    listenSelector: (frame.getAttribute(FrameAttribute.ListenSelector) ?? DEFAULT_LISTEN_SELECTOR).trim(),
    documentEvents: splitCsv(frame.getAttribute(FrameAttribute.DocumentEvents) ?? ""),
    lazy: frame.hasAttribute(FrameAttribute.Lazy),
    pollMs: Math.max(0, toNumber(frame.getAttribute(FrameAttribute.PollMs), 0)),
    executeScripts:
      rawExecuteScripts === "added" || rawExecuteScripts === "all" ? rawExecuteScripts : "none",
    timeoutMs: Math.max(0, toNumber(frame.getAttribute(FrameAttribute.TimeoutMs), 12_000)),
    credentials: parseCredentials(frame.getAttribute(FrameAttribute.Credentials)),
    broadcastMode,
    broadcastEventPrefix,
    broadcastMaxHop: Math.max(1, toNumber(frame.getAttribute(FrameAttribute.BroadcastMaxHop), 1)),
    broadcastReemit: frame.hasAttribute(FrameAttribute.BroadcastReemit)
      ? parseBoolean(frame.getAttribute(FrameAttribute.BroadcastReemit) ?? "")
      : false,
    listenRefreshFrom: listenRefreshFrom.filter((item) => item !== "*" && item !== "any"),
    listenRefreshAny,
    listenRefreshUrlMode,
    listenRefreshQueryKeys,
  };
}

/**
 * Parses `listen-history` CSV tokens into supported history actions.
 *
 * @param raw - Raw `listen-history` attribute value.
 * @returns Normalized history action tokens.
 */
function parseListenHistory(raw: string | null): FrameConfig["listenHistory"] {
  if (raw === null) return [];

  const normalized = raw.trim().toLowerCase();
  if (!normalized || parseBoolean(normalized)) {
    return ["push", "pop"];
  }

  const tokens = splitCsv(normalized);
  const out = new Set<FrameConfig["listenHistory"][number]>();
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === "push" || token === "replace" || token === "pop") {
      out.add(token);
    }
  }

  return Array.from(out);
}
